import { Component, ErrorInfo, ReactNode } from 'react';
import {
  Container,
  Paper,
  Box,
  Typography,
  Alert,
  Button,
  Collapse,
} from '@mui/material';
import { ErrorOutline, Refresh, BugReport } from '@mui/icons-material';
import { themeColors } from '../../utils/theme';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallbackTitle?: string;
  fallbackMessage?: string;
  showRetry?: boolean;
  showErrorDetails?: boolean;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showDetails: boolean;
}

/**
 * React Error Boundary component for graceful error handling
 * 
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI instead of crashing.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    });
  };

  toggleErrorDetails = () => {
    this.setState(prevState => ({
      showDetails: !prevState.showDetails,
    }));
  };

  render() {
    const {
      children,
      fallbackTitle = 'Something went wrong',
      fallbackMessage = 'An unexpected error occurred. Please try refreshing the page or contact support if the problem persists.',
      showRetry = true,
      showErrorDetails = process.env.NODE_ENV === 'development',
    } = this.props;

    const { hasError, error, errorInfo, showDetails } = this.state;

    if (hasError) {
      return (
        <Container maxWidth="lg">
          <Box sx={{ mt: 4, mb: 4 }}>
            <Paper
              elevation={3}
              sx={{
                p: 4,
                bgcolor: themeColors.paper,
                borderRadius: 2,
                textAlign: 'center',
              }}
            >
              {/* Error Icon */}
              <ErrorOutline
                sx={{
                  fontSize: 64,
                  color: 'error.main',
                  mb: 2,
                }}
              />

              {/* Error Title */}
              <Typography
                variant="h5"
                component="h1"
                sx={{ mb: 2, color: themeColors.text }}
              >
                {fallbackTitle}
              </Typography>

              {/* Error Message */}
              <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
                {fallbackMessage}
              </Alert>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 3 }}>
                {showRetry && (
                  <Button
                    variant="contained"
                    startIcon={<Refresh />}
                    onClick={this.handleRetry}
                    sx={{
                      bgcolor: themeColors.primary,
                      '&:hover': {
                        bgcolor: themeColors.accent,
                      },
                    }}
                  >
                    Try Again
                  </Button>
                )}

                {showErrorDetails && error && (
                  <Button
                    variant="outlined"
                    startIcon={<BugReport />}
                    onClick={this.toggleErrorDetails}
                    sx={{
                      borderColor: themeColors.primary,
                      color: themeColors.primary,
                      '&:hover': {
                        borderColor: themeColors.accent,
                        bgcolor: 'rgba(65, 200, 196, 0.04)',
                      },
                    }}
                  >
                    {showDetails ? 'Hide' : 'Show'} Error Details
                  </Button>
                )}
              </Box>

              {/* Error Details (Development Only) */}
              {showErrorDetails && error && (
                <Collapse in={showDetails}>
                  <Box
                    sx={{
                      textAlign: 'left',
                      bgcolor: 'grey.100',
                      p: 2,
                      borderRadius: 1,
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      overflow: 'auto',
                      maxHeight: 300,
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                      Error Message:
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2, color: 'error.main' }}>
                      {error.message}
                    </Typography>

                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                      Stack Trace:
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                      {error.stack}
                    </Typography>

                    {errorInfo && errorInfo.componentStack && (
                      <>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                          Component Stack:
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {errorInfo.componentStack}
                        </Typography>
                      </>
                    )}
                  </Box>
                </Collapse>
              )}

              {/* Help Text */}
              <Typography variant="body2" sx={{ color: themeColors.textSecondary, mt: 2 }}>
                If the problem persists, please contact support with the error details above.
              </Typography>
            </Paper>
          </Box>
        </Container>
      );
    }

    return children;
  }
}

export default ErrorBoundary;